import { AcmErrorCode } from '@dce-front/types-acm';
import { fetch } from '@dce-front/util-fetch';
import type { LogApiMetadata } from '@dce-front/util-logger';
import { Service } from '@dce-front/util-logger';
import type { AcmSdkConfig } from '../config/types';
import type { AcmApi } from '../constants/paths';
import { AcmError } from '../error/AcmError';
import { AcmLockedSafetyCodeError } from '../error/AcmLockedSafetyCodeError';
import { logError } from '../helpers/logError';
import { getFilledApiUrl } from './getFilledApiUrl';
import { isAcmApiError } from './isAcmApiError';

type FetchAcmParameters = {
  acmSdkConfig: AcmSdkConfig;
  api: AcmApi;
};

/**
 * Helper in charge of:
 * - Fetching the given url (+ log info)
 * - Throwing an error if the response is not ok (+ log error)
 *
 * @private
 */
export const fetchAcm = async ({
  acmSdkConfig,
  api,
}: FetchAcmParameters): Promise<object> => {
  const {
    appKeyLog,
    endpoint,
    fetchOptions,
    logger,
    logMetadata,
    offerLocation,
    offerZone,
    requestId,
  } = acmSdkConfig;

  const url = getFilledApiUrl({
    api,
    endpoint,
    offerLocation,
    offerZone,
  });

  const baseErrorLogs: LogApiMetadata = {
    ...logMetadata,
    endpoint: url,
    offerLocation,
    offerZone,
    route: api,
    startAt: Date.now(),
    req: { appData: { appKey: appKeyLog }, ...(requestId && { requestId }) },
  } as const;

  try {
    const response = await fetch(url, fetchOptions);

    logger.info(
      `ACM ${api} ${response.status}`,
      logger.generateApiMetadata(Service.Acm, api, {
        ...baseErrorLogs,
        statusCode: response.status,
      }),
    );

    const jsonResponse = await response.json();

    if (isAcmApiError(jsonResponse)) {
      const { errorCode, errorDescription, statusCode } = jsonResponse;

      if (
        errorCode === AcmErrorCode['Code_401.2'] &&
        (api === 'checkParentalCode' || api === 'checkPurchaseCode')
      ) {
        throw new AcmLockedSafetyCodeError();
      }

      throw new AcmError(errorCode, errorDescription, statusCode);
    }

    if (
      response.ok &&
      (api === 'getParentalCodeStatus' ||
        api === 'getPurchaseCodeStatus' ||
        api === 'checkParentalCode' ||
        api === 'checkPurchaseCode')
    ) {
      if (jsonResponse.status === 204) {
        throw new Error(
          `ACM ${api} ${jsonResponse.status}, code does not exist`,
        );
      }
    }

    if (!response.ok) {
      throw new Error(`Error while fetching ACM API: ${response.status}`);
    }

    return jsonResponse;
  } catch (error) {
    logError({ error, logger, baseErrorLogs, api });

    throw error;
  }
};
